import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Trophy, Clock, BarChart3, Phone, Mail, MessageCircle, ArrowRight, CheckCircle2, PlayCircle, FileText, Loader2 } from 'lucide-react';
import { hcApi, hcError, formatDuration } from './api';
import { HcLayout } from './HcLayout';
import { CourseCard } from './HcCourseBits';
import { PathwaysTracker } from './HcPathwaysTracker';

const BANNER = '/helpconnect/banner2.webp';

const Stat = ({ icon: Icon, value, label, testId }) => (
  <div className="bg-white rounded-2xl p-5 flex items-center gap-4 border border-gray-100 shadow-sm">
    <div className="w-14 h-14 rounded-full bg-[#F6EAF5] flex items-center justify-center shrink-0"><Icon className="w-6 h-6 text-[#922A8C]" /></div>
    <div>
      <div className="text-3xl font-extrabold text-[#363636]" data-testid={testId}>{value}</div>
      <div className="text-gray-500 text-sm">{label}</div>
    </div>
  </div>
);

const CaseManagerCard = ({ cm }) => {
  const navigate = useNavigate();
  return (
  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="hc-case-manager-card">
    <h2 className="text-xl font-bold text-[#363636] mb-4">Your Case Manager</h2>
    {cm ? (
      <>
        <div className="flex gap-4 items-center">
          <div className="w-16 h-16 rounded-full bg-[#F6EAF5] text-[#922A8C] font-bold text-xl flex items-center justify-center shrink-0">
            {cm.name?.split(' ').map((p) => p[0]).slice(0, 2).join('')}
          </div>
          <div className="min-w-0">
            <div className="font-bold text-lg text-[#363636]" data-testid="hc-case-manager-name">{cm.name}</div>
            <div className="text-sm text-gray-500">{cm.title}</div>
            {cm.phone && <a href={`tel:${cm.phone}`} className="flex items-center gap-2 text-sm text-gray-600 mt-1 hover:text-[#922A8C]"><Phone className="w-4 h-4 text-[#922A8C]" />{cm.phone}</a>}
            {cm.email && <a href={`mailto:${cm.email}`} className="flex items-center gap-2 text-sm text-gray-600 mt-1 truncate hover:text-[#922A8C]" data-testid="hc-case-manager-email"><Mail className="w-4 h-4 text-[#922A8C] shrink-0" /><span className="truncate">{cm.email}</span></a>}
          </div>
        </div>
        <button onClick={() => navigate('/helpconnect/messages')} className="mt-5 w-full h-11 rounded-xl bg-[#922A8C] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#7a2275] transition-colors" data-testid="hc-send-message-btn">
          <MessageCircle className="w-4 h-4" /> Send Message
        </button>
      </>
    ) : <p className="text-gray-500 text-sm">A case manager hasn't been assigned yet.</p>}
  </div>
  );
};

const ACT_ICON = { completed: CheckCircle2, viewed: FileText };
const RecentActivity = ({ items }) => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="hc-recent-activity">
    <h2 className="text-xl font-bold text-[#363636] mb-4">Recent Activity</h2>
    {items.length === 0 && <p className="text-sm text-gray-500">Your learning activity will show up here.</p>}
    <ul className="space-y-4">
      {items.map((a, i) => {
        const Icon = a.type === 'completed' && a.lesson_type === 'VIDEO' ? PlayCircle : ACT_ICON[a.type] || FileText;
        return (
          <li key={i} className="flex gap-3">
            <Icon className={`w-6 h-6 shrink-0 ${a.type === 'completed' ? 'text-green-600' : 'text-[#922A8C]'}`} />
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#363636] truncate">{a.type === 'completed' ? 'Completed' : 'Viewed'}: {a.title}</div>
              <div className="text-xs text-gray-500">{new Date(a.at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
            </div>
          </li>
        );
      })}
    </ul>
  </div>
);

export default function HcDashboardPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    hcApi.get('/dashboard').then(({ data }) => setData(data)).catch((e) => setError(hcError(e)));
  }, []);

  return (
    <HcLayout>
      {!data && !error && <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#922A8C]" /></div>}
      {error && <p className="text-red-600" data-testid="hc-dashboard-error">{error}</p>}
      {data && (
        <div className="space-y-6 hc-fade-up" data-testid="hc-dashboard">
          <section className="dark-keep relative overflow-hidden rounded-3xl bg-[#F1E8FB] border border-[#922A8C]/10 2xl:aspect-[2000/454] 2xl:min-h-[200px] 2xl:flex 2xl:items-center" data-testid="hc-welcome-banner">
            <div className="relative z-10 p-8 2xl:max-w-[44%]">
              <h1 className="text-4xl sm:text-5xl font-extrabold text-[#363636]" data-testid="hc-welcome">Welcome back, {data.me.first_name}!</h1>
              <p className="text-xl font-semibold text-[#922A8C] mt-2">You're on your way to a brighter future.</p>
              <p className="text-gray-600 mt-2 max-w-lg">Access your courses, track your progress, and develop the skills needed to achieve your career goals.</p>
            </div>
            <img src={BANNER} alt="Every step you take today is a step toward a brighter tomorrow." className="hidden 2xl:block absolute inset-0 w-full h-full object-cover object-right" data-testid="hc-banner-image" />
            <img src={BANNER} alt="Every step you take today is a step toward a brighter tomorrow." className="hidden md:block 2xl:hidden w-full h-auto" data-testid="hc-banner-image-stacked" />
          </section>

          <section className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <Stat icon={BookOpen} value={data.stats.active_courses} label="Active Courses" testId="hc-stat-active-courses" />
            <Stat icon={Trophy} value={data.stats.lessons_completed} label="Lessons Completed" testId="hc-stat-lessons-completed" />
            <Stat icon={Clock} value={formatDuration(data.stats.learning_seconds)} label="Learning Time" testId="hc-stat-learning-time" />
            <Stat icon={BarChart3} value={`${data.stats.overall_progress}%`} label="Overall Progress" testId="hc-stat-overall-progress" />
          </section>

          <PathwaysTracker modules={data.pathways} />

          <section className="grid xl:grid-cols-[minmax(0,1fr)_340px] gap-6 min-w-0">
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-100 shadow-sm min-w-0">
              <div className="flex flex-wrap gap-2 items-center justify-between mb-5">
                <h2 className="text-2xl font-bold text-[#363636]">My Courses</h2>
                <Link to="/helpconnect/courses" className="text-[#922A8C] font-semibold flex items-center gap-1 hover:underline" data-testid="hc-view-all-courses">View All Courses <ArrowRight className="w-4 h-4" /></Link>
              </div>
              {data.courses.length === 0 ? (
                <p className="text-gray-500" data-testid="hc-no-courses">You don't have any courses yet. Your case manager will enroll you when you're ready.</p>
              ) : (
                <div className="grid sm:grid-cols-2 2xl:grid-cols-3 gap-5">
                  {data.courses.slice(0, 3).map((c, i) => <CourseCard key={c.id} course={c} primary={i === 0} />)}
                </div>
              )}
            </div>
            <div className="space-y-6">
              <CaseManagerCard cm={data.case_manager} />
              <RecentActivity items={data.recent_activity} />
            </div>
          </section>
        </div>
      )}
    </HcLayout>
  );
}
